import { collectPipelineRefs, type StageRole } from "./pipelineLint";

// The toolbar's Source → Transform → Sink progress (Figma 14896-92090): a
// step is completed once the pipeline has something real in that role.
// Transform is the optional step — a source-to-sink pipeline runs without
// one — so it also reads as completed once both ends are in place.
export type StageProgress = Record<StageRole, boolean>;

const NO_ROLES: ReadonlySet<StageRole> = new Set();

/** The Stages view: a step is completed once its section holds a row. */
export function stageProgressFromStaged(staged: {
  sources: ReadonlyArray<unknown>;
  transforms: ReadonlyArray<unknown>;
  sinks: ReadonlyArray<unknown>;
}): StageProgress {
  return withOptionalTransform({
    source: staged.sources.length > 0,
    transform: staged.transforms.length > 0,
    sink: staged.sinks.length > 0,
  });
}

/** The Full pipeline view: a step is completed once an entry of its role
 *  names both a service and a resource — the template's "" placeholders
 *  don't count, and neither does code that doesn't parse — and the lint
 *  pass found nothing wrong with that role (`errored`, from
 *  erroredStageRoles). */
export function stageProgressFromCode(
  code: string,
  errored: ReadonlySet<StageRole> = NO_ROLES,
): StageProgress {
  const filled = collectPipelineRefs(parseDoc(code)).filter(
    (ref) => ref.kind === "stage" && ref.resource !== "",
  );
  const done = (role: StageRole, refRole: "source" | "middleware" | "sink") =>
    !errored.has(role) && filled.some((ref) => ref.role === refRole);
  return withOptionalTransform({
    source: done("source", "source"),
    transform: done("transform", "middleware"),
    sink: done("sink", "sink"),
  });
}

function parseDoc(code: string): unknown {
  try {
    return JSON.parse(code);
  } catch {
    return null;
  }
}

function withOptionalTransform(progress: StageProgress): StageProgress {
  const endsDone = progress.source && progress.sink;
  return { ...progress, transform: progress.transform || endsDone };
}
